import React, { useEffect, useState } from "react";
import { Modal } from "../../../../../../../components/core/Modal/Modal";
import {
  Button,
  ButtonVariants,
} from "../../../../../../../components/core/Button/Button";
import {
  ADD_CERTIFICATIONS_LABEL,
  CANCEL_LABEL,
  CERTIFICATION_LABEL,
  CERTIFICATION_NAME_LABEL,
  CONFIRM_LABEL,
  DATE_ACHIEVEMENT_LABEL,
  MUST_BE_AFTER_THAN_LABEL,
  EDIT_CERTIFICATION_LABEL,
  INVALID_DATE_LABEL,
  IS_REQUIRED_LABEL,
  OTHER_LABEL,
  VALIDITY_EXPIRATION_LABEL,
  IS_NOT_VALID_LABEL,
} from "../../../../../../../constants/Dictionary";
import { ReactComponent as CheckIcon } from "../../../../../../../icons/check.svg";
import { ReactComponent as CloseIcon } from "../../../../../../../icons/close.svg";
import {
  Select,
  SelectOption,
  SelectVariantEnum,
} from "../../../../../../../components/core/Select/Select";
import TextInput from "../../../../../../../components/core/TextInput/TextInput";
import { FileUploader } from "../../../../../../../components/core/UploadFile/FileUploader";
import { FileUploaderSizeEnum } from "../../../../../../../types/AppTypes";
import { StyledHistoryCertificationModal } from "./HistoryCertificationModal.styled";
import { CalendarInput } from "../../../../../../../components/core/CalendarInput/CalendarInput";
import {
  AddCollaboratorsHistoryCertification,
  CollaboratorDetails,
  CollaboratorHistoryCertification,
  UpdateCollaboratorsHistoryCertification,
} from "../../../../../../../types/api/collaborators.type";
import { Loader } from "../../../../../../../components/core/Loader/Loader";
import { FormField } from "../../../../../../../components/core/FormField/FormField";
import { Controller, Resolver, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { EN_DATE_FORMAT } from "../../../../../../../utils/date/date.utils";
import dayjs from "dayjs";
import { checkHasMoreData } from "../../../../../../../utils/pagination.utils";
import { useDebounce } from "use-debounce";
import { TextBox } from "../../../../../../../components/TextBoxGrid/TextBoxGrid.component";
import {
  useCertificationHistoryAdd,
  useCertificationHistoryCertificate,
  useCertificationHistoryUpdate,
  useCertificationList,
} from "../../../../../../../hooks/formations.hook";

type HistoryCertificationModalProps = {
  certification?: CollaboratorHistoryCertification | null;
  isOpen: boolean;
  onSave: () => void;
  onClose: () => void;
  certificateApi?: () => Promise<ArrayBuffer>;
  collaborator: CollaboratorDetails;
  certificateId?: number;
};

type FormData = {
  certificationId?: number | null;
  name?: string;
  deliveryDate: Date | null;
  expirationDate?: Date | null;
  certificateFile: {
    file?: File | null;
    name?: string | null;
  } | null;
};

export const HistoryCertificationModal = ({
  certification,
  isOpen,
  onSave,
  onClose,
  collaborator,
  certificateId,
}: HistoryCertificationModalProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [searchDebouncedValue] = useDebounce(searchValue, 500);
  const [pageNumber, setPageNumber] = useState(0);
  const [certificationOptions, setCertificationOptions] = useState<
    SelectOption<number>[]
  >([]);
  const profileId = collaborator.id;

  const { data: certificate } = useCertificationHistoryCertificate({
    profileId,
    isOpen,
    certificateId,
  });

  const { data: certificationData, isLoading: isLoadingCertification } =
    useCertificationList({
      profileId,
      pageNumber,
      searchDebouncedValue,
    });

  const {
    mutateAsync: updateCertification,
    isPending: pendingUpdateCertification,
  } = useCertificationHistoryUpdate(profileId);

  const { mutateAsync: addCertification, isPending: pendingAddCertification } =
    useCertificationHistoryAdd(profileId);

  useEffect(() => {
    if (!certificationData) return;
    const newOptions = certificationData.data.map((course) => ({
      value: course.id,
      label: course.name,
    }));
    if (pageNumber === 0) {
      setCertificationOptions([
        { value: -1, label: OTHER_LABEL },
        ...newOptions,
      ]);
    } else {
      setCertificationOptions((prev) => [...prev, ...newOptions]);
    }
    setHasMore(checkHasMoreData(certificationData.pagination));
  }, [certificationData]);

  useEffect(() => {
    setPageNumber(0);
  }, [searchDebouncedValue]);

  const closeModal = () => {
    onClose();
  };

  const schema = (isNewCertification: boolean) =>
    yup.object({
      certificationId: yup
        .number()
        .nullable()
        .when([], {
          is: () => isNewCertification,
          then: (field) =>
            field.required(`${CERTIFICATION_LABEL} ${IS_REQUIRED_LABEL}`),
          otherwise: (field) => field.notRequired(),
        }),
      name: yup.string().when(["certificationId"], {
        is: (certificationId: number) =>
          certificationId === -1 || !isNewCertification,
        then: (field) =>
          field.required(`${CERTIFICATION_NAME_LABEL} ${IS_REQUIRED_LABEL}`),
        otherwise: (field) => field.notRequired(),
      }),
      deliveryDate: yup
        .date()
        .nullable()
        .typeError(`${INVALID_DATE_LABEL}`)
        .max(new Date(), `${DATE_ACHIEVEMENT_LABEL} ${IS_NOT_VALID_LABEL}`)
        .required(`${DATE_ACHIEVEMENT_LABEL} ${IS_REQUIRED_LABEL}`),
      expirationDate: yup
        .date()
        .nullable()
        .typeError(`${INVALID_DATE_LABEL}`)
        .test(
          "is-after-deliveryDate",
          `La ${VALIDITY_EXPIRATION_LABEL} ${MUST_BE_AFTER_THAN_LABEL} ${DATE_ACHIEVEMENT_LABEL}.`,
          function (expirationDate) {
            const { deliveryDate } = this.parent;
            if (!expirationDate || !deliveryDate) return true;
            const oneDay = 24 * 60 * 60 * 1000;
            return expirationDate.getTime() - deliveryDate.getTime() >= oneDay;
          },
        ),
      certificateFile: yup
        .object({
          file: yup.mixed<File>().nullable(),
          name: yup.string().nullable(),
        })
        .nullable(),
    });

  const {
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(schema(!certification)) as Resolver<FormData>,
  });

  const watchCertification = watch("certificationId");
  const watchCertificationDeliveryDate = watch("deliveryDate");

  const onSubmit = async (data: FormData) => {
    if (certification) {
      const updatedBody: UpdateCollaboratorsHistoryCertification = {
        certificationName: !certification?.external
          ? certification?.certification.name
          : data.name!,
        deliveryDate: dayjs(data.deliveryDate).format(EN_DATE_FORMAT),
        expirationDate: data.expirationDate
          ? dayjs(data.expirationDate).format(EN_DATE_FORMAT)
          : null,
      };

      await updateCertification({
        certificationId: certification.id,
        updateBody: updatedBody,
        data,
      });
    } else {
      const addBody: AddCollaboratorsHistoryCertification = {
        certificationId:
          data.certificationId !== undefined
            ? data.certificationId === -1
              ? null
              : data.certificationId
            : null,
        certificationName: data.certificationId === -1 ? data.name! : null!,
        deliveryDate: dayjs(data.deliveryDate).format(EN_DATE_FORMAT),
        expirationDate: data.expirationDate
          ? dayjs(data.expirationDate).format(EN_DATE_FORMAT)
          : null,
        external: data.certificationId === -1,
      };
      await addCertification({ addBody, data });
    }
    closeModal();
    onSave();
  };

  useEffect(() => {
    if (certification) {
      reset({
        name: certification.certification.name,
        deliveryDate: dayjs(certification.deliveryDate).toDate(),
        expirationDate: certification.expirationDate
          ? dayjs(certification.expirationDate).toDate()
          : null,
        certificateFile: {
          name: certification.fileName,
        },
      });
    } else {
      reset({
        certificationId: null,
        name: "",
        deliveryDate: null,
        expirationDate: null,
        certificateFile: null,
      });
    }
  }, [certification, isOpen]);

  const canEditName = certification?.external || watchCertification === -1;

  const deliveryDate = watch("deliveryDate");

  const pendingModal = pendingUpdateCertification || pendingAddCertification;

  return (
    <Modal
      fullSize={false}
      label={
        certification ? EDIT_CERTIFICATION_LABEL : ADD_CERTIFICATIONS_LABEL
      }
      isOpen={isOpen}
      leftButton={
        <Button
          label={CANCEL_LABEL}
          variant={ButtonVariants.SECONDARY}
          icon={<CloseIcon />}
          onClick={closeModal}
          disabled={pendingModal}
        />
      }
      rightButton={
        <Button
          label={CONFIRM_LABEL}
          variant={ButtonVariants.PRIMARY}
          icon={<CheckIcon />}
          disabled={pendingModal || !isDirty}
          form="historyCertificationForm"
        />
      }
    >
      <StyledHistoryCertificationModal>
        <Loader loading={pendingModal}>
          <form id="historyCertificationForm" onSubmit={handleSubmit(onSubmit)}>
            <div className="input-row">
              {certification && !certification.external && (
                <div className="mb">
                  <TextBox
                    label={CERTIFICATION_NAME_LABEL}
                    text={certification.certification.name}
                  />
                </div>
              )}
              {!certification && (
                <FormField error={errors.certificationId?.message}>
                  <Controller
                    name="certificationId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        variant={SelectVariantEnum.DEFAULT}
                        label={CERTIFICATION_LABEL}
                        options={certificationOptions}
                        {...field}
                        onSearch={setSearchValue}
                        searchValue={searchValue}
                        loading={
                          isLoadingCertification ||
                          certificationOptions?.length === 0
                        }
                        canLoadMore={hasMore}
                        fetchMore={() => setPageNumber((p) => p + 1)}
                        withSearch
                      />
                    )}
                  />
                </FormField>
              )}
              {canEditName && (
                <FormField error={errors.name?.message}>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label={CERTIFICATION_NAME_LABEL}
                        maxLength={255}
                        {...field}
                      />
                    )}
                  />
                </FormField>
              )}
            </div>
            <div className="input-row">
              <FormField error={errors.deliveryDate?.message}>
                <Controller
                  name="deliveryDate"
                  control={control}
                  render={({ field }) => (
                    <CalendarInput
                      label={DATE_ACHIEVEMENT_LABEL}
                      maxDate={new Date()}
                      {...field}
                    />
                  )}
                />
              </FormField>
              {((!certification && watchCertificationDeliveryDate) ||
                certification) && (
                <FormField error={errors.expirationDate?.message}>
                  <Controller
                    name="expirationDate"
                    control={control}
                    render={({ field }) => (
                      <CalendarInput
                        label={VALIDITY_EXPIRATION_LABEL}
                        minDate={
                          deliveryDate && !isNaN(deliveryDate.getTime())
                            ? deliveryDate
                            : undefined
                        }
                        {...field}
                      />
                    )}
                  />
                </FormField>
              )}
            </div>
            <FormField error={errors.certificateFile?.message}>
              <Controller
                name="certificateFile"
                control={control}
                render={({ field }) => {
                  return (
                    <FileUploader
                      size={FileUploaderSizeEnum.REGULAR}
                      onFileUpload={(file: File) => {
                        field.onChange({ file: file, name: file.name });
                      }}
                      value={field.value?.file || null}
                      fileName={field.value?.name ?? ""}
                      fileSize={
                        field.value?.file
                          ? field.value.file.size
                          : certificate
                            ? certificate.byteLength
                            : undefined
                      }
                      handleDeleteFile={() => {
                        field.onChange(null);
                      }}
                    />
                  );
                }}
              />
            </FormField>
          </form>
        </Loader>
      </StyledHistoryCertificationModal>
    </Modal>
  );
};
